import React from 'react';
import { Star, Eye, Zap, Plus, ImagePlus, Link } from 'lucide-react';
import { formatPrice } from '../data/products';

const cardStyle = {
  background: 'white',
  borderRadius: 'var(--radius)',
  overflow: 'hidden',
  boxShadow: 'var(--shadow-sm)',
  transition: 'transform 0.25s, box-shadow 0.25s',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--green-border)',
};

function StarRating({ rating }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {[1,2,3,4,5].map(i => (
        <Star
          key={i}
          size={13}
          fill={i <= Math.round(rating) ? 'var(--orange)' : 'none'}
          color={i <= Math.round(rating) ? 'var(--orange)' : 'var(--gray-400)'}
        />
      ))}
      <span style={{ fontSize: '12px', color: 'var(--gray-400)', marginLeft: '2px' }}>{rating}</span>
    </div>
  );
}

function ProductCard({ product, onView }) {
  return (
    <div
      style={cardStyle}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
      }}
    >
      {/* Imagen */}
      <div style={{ position: 'relative', overflow: 'hidden', background: 'var(--green-pale)' }}>
        <img
          src={product.image}
          alt={product.name}
          style={{
            width: '100%', height: '200px', objectFit: 'cover',
            transition: 'transform 0.4s', display: 'block',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.06)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        />
        {product.badge && (
          <span style={{
            position: 'absolute', top: '12px', left: '12px',
            background: product.badgeColor, color: 'white',
            fontSize: '11px', fontWeight: 800,
            padding: '4px 10px', borderRadius: '50px', letterSpacing: '0.3px',
          }}>
            {product.badge}
          </span>
        )}
        <button
          onClick={() => onView(product)}
          style={{
            position: 'absolute', bottom: '12px', right: '12px',
            background: 'white', border: 'none', borderRadius: '50%',
            width: '36px', height: '36px', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'var(--green-dark)', cursor: 'pointer',
            boxShadow: 'var(--shadow-md)', opacity: 0, transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
          title="Vista rápida"
        >
          <Eye size={16} />
        </button>
      </div>

      {/* Contenido */}
      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-800)', lineHeight: 1.3 }}>
          {product.name}
        </h3>
        <StarRating rating={product.rating} />
        <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>{product.reviews} reseñas</span>

        <div style={{ marginTop: 'auto', paddingTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span style={{ fontSize: '20px', fontWeight: 900, color: 'var(--green-dark)' }}>
              {formatPrice(product.price)}
            </span>
            {product.originalPrice && (
              <span style={{ fontSize: '13px', color: 'var(--gray-400)', textDecoration: 'line-through' }}>
                {formatPrice(product.originalPrice)}
              </span>
            )}
          </div>

          <a
            href={product.paymentLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '7px',
              width: '100%',
              background: 'var(--orange)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '11px',
              fontFamily: 'var(--font-main)',
              fontWeight: 800,
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'background 0.2s, transform 0.15s',
              boxSizing: 'border-box',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = '#d4740a';
              e.currentTarget.style.transform = 'scale(1.03)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'var(--orange)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <Zap size={15} /> Comprar ahora
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Tarjeta placeholder ─────────────────────────────────────────────────────
function PlaceholderCard({ slotNumber }) {
  return (
    <div style={{
      borderRadius: 'var(--radius)',
      border: '2px dashed var(--green-border)',
      background: 'linear-gradient(135deg, var(--green-pale) 0%, #f0faf3 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '360px',
      padding: '32px 20px',
      textAlign: 'center',
      transition: 'border-color 0.2s, background 0.2s',
      cursor: 'default',
    }}
    onMouseEnter={e => {
      e.currentTarget.style.borderColor = 'var(--green-mid)';
      e.currentTarget.style.background = 'linear-gradient(135deg, #d8f0de 0%, #e8faf0 100%)';
    }}
    onMouseLeave={e => {
      e.currentTarget.style.borderColor = 'var(--green-border)';
      e.currentTarget.style.background = 'linear-gradient(135deg, var(--green-pale) 0%, #f0faf3 100%)';
    }}
    >
      <div style={{
        width: '72px', height: '72px', borderRadius: '50%',
        background: 'white', border: '2px solid var(--green-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: '16px', boxShadow: 'var(--shadow-sm)',
      }}>
        <Plus size={30} color="var(--green-mid)" />
      </div>
      <div style={{ fontSize: '11px', fontWeight: 800, letterSpacing: '1px', color: 'var(--green-mid)', textTransform: 'uppercase', marginBottom: '8px' }}>
        Slot #{slotNumber}
      </div>
      <h3 style={{ fontSize: '16px', fontWeight: 800, color: 'var(--green-dark)', marginBottom: '10px' }}>
        Agregar producto
      </h3>
      <p style={{ fontSize: '13px', color: 'var(--gray-600)', lineHeight: 1.6, marginBottom: '20px', maxWidth: '200px' }}>
        Edita <code style={{ background: 'white', color: 'var(--green-dark)', padding: '2px 6px', borderRadius: '6px', fontSize: '12px', fontWeight: 700, border: '1px solid var(--green-border)' }}>src/data/products.js</code> y rellena los campos de este slot
      </p>
      <div style={{ background: 'white', borderRadius: '12px', padding: '14px 16px', border: '1px solid var(--green-border)', width: '100%', maxWidth: '240px', textAlign: 'left' }}>
        <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--green-dark)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Campos a completar:
        </div>
        {[
          { icon: <ImagePlus size={12} />, label: 'image', desc: 'URL de la imagen' },
          { icon: <Link size={12} />,      label: 'paymentLink', desc: 'Link de pago directo' },
          { icon: <span style={{ fontSize: '11px' }}>💲</span>, label: 'price', desc: 'Precio en CLP' },
          { icon: <span style={{ fontSize: '11px' }}>📝</span>, label: 'name', desc: 'Nombre del producto' },
        ].map((field, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < 3 ? '7px' : 0 }}>
            <span style={{ color: 'var(--green-mid)', display: 'flex', alignItems: 'center' }}>{field.icon}</span>
            <span>
              <code style={{ fontSize: '11px', color: 'var(--green-dark)', fontWeight: 700 }}>{field.label}</code>
              <span style={{ fontSize: '11px', color: 'var(--gray-400)', marginLeft: '4px' }}>→ {field.desc}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Grid principal ───────────────────────────────────────────────────────────
export default function ProductGrid({ products, placeholders = [], onProductClick, showPlaceholders = true }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
      gap: '24px',
    }}>
      {products.map(product => (
        <ProductCard
          key={product.id}
          product={product}
          onView={onProductClick}
        />
      ))}
      {showPlaceholders && placeholders.map((p, idx) => (
        <PlaceholderCard key={p.id} slotNumber={idx + 1} />
      ))}
    </div>
  );
}
